import { z } from 'zod';

/**
 * Per-tenant geometry + typography settings for the native app experience.
 * Stored at `business_settings.theme_config.native_design`.
 *
 * Colors are NOT in this schema — they live in dedicated columns
 * (`app_background_color`, `app_text_color`).
 */
export const nativeDesignConfigSchema = z.object({
  /** Corner radius for cards / images. Defaults follow docs/design-system.md §13. */
  radius: z.number().min(0).max(32).optional(),
  /** Corner radius for hero / modals. */
  radiusLg: z.number().min(0).max(48).optional(),
  /** Typography family preset. 'serif' enables Cormorant Garamond display. */
  fontFamily: z.enum(['system', 'serif']).optional(),
  /** Spacing scale used by section components. */
  density: z.enum(['compact', 'regular', 'spacious']).optional(),
});

export type NativeDesignConfig = z.infer<typeof nativeDesignConfigSchema>;

export const DEFAULT_NATIVE_DESIGN = {
  radius: 12,
  radiusLg: 16,
  fontFamily: 'system',
  density: 'regular',
} satisfies NativeDesignConfig;

export function parseNativeDesignConfig(raw: unknown): NativeDesignConfig {
  if (!raw) return DEFAULT_NATIVE_DESIGN;
  const result = nativeDesignConfigSchema.safeParse(raw);
  return result.success
    ? { ...DEFAULT_NATIVE_DESIGN, ...result.data }
    : DEFAULT_NATIVE_DESIGN;
}
